import { Carousel } from '@design-system';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import latinize from 'latinize';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import { storefrontClient } from '../../../apollo/shopify/storefrontClient';
import { DEFAULT_COUNTRY_CODE } from '../../../constants/locations';
import { useIsSubscribed } from '../../../hooks/subscription/useIsSubscribed';
import { useCountryCode } from '../../../hooks/useCountryCode';
import {
  GeneratedSubscriptionType,
  useGetDrawByProductIDsQuery,
} from '../../../types/generated';
import {
  ShopifyGenCountryCode,
  useProductsByTagQuery,
} from '../../../types/shopifygen';
import { TrackCarouselSlide } from '../../../utils/analytics';
import {
  isUpcoming,
  mapShopifyProductsToProducts,
  sortProducts,
} from '../../../utils/product';
import ProductTile from '../../ProductTile';

export type FilterValue = string;

export interface FilterOperation {
  operation: 'AND' | 'OR';
  items: (FilterOperation | FilterValue)[];

  // If set to true, it will apple a `not` to all tags provided in the items
  exclude?: boolean;
}

export type Filter = FilterOperation | FilterValue;

type Props = {
  analytics?: Analytics;
  filter: Filter;
  heading: string;
  numberOfProducts?: number;
  order?: 'ASC' | 'DESC';
  placeholder?: ReactElement;
  plannedReleaseTile?: ReactElement;
  productToExclude?: string;
};

const filterProductToExclude = (products: Array<any>, productId: string) => {
  return products.filter(
    (product: { node: Product }) => product.node.id !== productId,
  );
};

const ProductsCarousel: React.FC<Props> = ({
  analytics,
  filter,
  heading,
  numberOfProducts = 12,
  order = 'DESC',
  placeholder = null,
  plannedReleaseTile,
  productToExclude,
}) => {
  const [countryCode] = useCountryCode();
  const [productsToShow, setProductsToShow] = useState<Product[]>([]);
  const filters = parseFilter({
    operation: 'AND',
    items: ['Listing page', filter],
  });

  const productsByTag = useProductsByTagQuery({
    client: storefrontClient,
    variables: {
      country: DEFAULT_COUNTRY_CODE as ShopifyGenCountryCode,
      filters,
      limit: numberOfProducts,
    },
  });

  const productIDs = productsToShow.map((p) => shopifyGraphqlIdToAdminId(p.id));
  const { data: draws } = useGetDrawByProductIDsQuery({
    variables: { productIDs },
    skip: productIDs.length === 0,
  });

  const upcomingProducts = productsToShow.filter((product) => {
    return isUpcoming(product);
  });

  const { checkIsSubscribed, refetch } = useIsSubscribed(
    upcomingProducts.map((product) => ({
      type: GeneratedSubscriptionType.Product,
      objectId: shopifyGraphqlIdToAdminId(product.id) || '',
    })),
  );

  useEffect(() => {
    let products = productsByTag.data?.products?.edges;
    if (products) {
      if (productToExclude) {
        products = filterProductToExclude(products, productToExclude);
      }

      const resolved = mapShopifyProductsToProducts(
        // @ts-ignore
        products.map((prod) => prod.node),
      ).filter((product) => !product.excludeFromRegions.includes(countryCode));
      setProductsToShow(sortProducts(resolved, order));
    }
  }, [countryCode, order, productToExclude, productsByTag]);

  const productTileComponents = useMemo(
    () =>
      productsToShow.map((product, index) => (
        <ProductTile
          key={product.id}
          analytics={
            analytics && {
              page: analytics.page,
              context: analytics.context,
              position: index,
            }
          }
          draw={draws?.getDrawByProductIDs?.find(
            (d) => d.productId === shopifyGraphqlIdToAdminId(product.id),
          )}
          isSubscribed={checkIsSubscribed(
            shopifyGraphqlIdToAdminId(product.id),
            GeneratedSubscriptionType.Product,
          )}
          onSubscribe={() => {
            refetch();
          }}
          product={product}
        />
      )),
    [productsToShow, draws?.getDrawByProductIDs],
  );

  const allTileComponents = [
    plannedReleaseTile,
    ...productTileComponents,
  ].filter((item) => !!item);

  const hasNoItems = !productsToShow || productsToShow.length === 0;

  if (hasNoItems && !placeholder) {
    return null;
  }

  return (
    <Carousel
      headline={heading}
      items={hasNoItems ? [placeholder] : allTileComponents}
      onSlide={(index: number) => {
        if (analytics && !hasNoItems) {
          TrackCarouselSlide({
            page: analytics.page,
            context: analytics.context,
            title: heading,
            position: index,
            carouselLength: productsToShow.length,
          });
        }
      }}
      perPage={4}
    />
  );
};

function parseFilter(filter: Filter, exclude?: boolean): string {
  if (typeof filter === 'string') {
    const tagPrefix = exclude ? 'tag_not' : 'tag';

    return `(${tagPrefix}:'${filter}' OR ${tagPrefix}:'${latinize(filter)}')`;
  }

  return `(${filter.items
    .map((i) => parseFilter(i, filter.exclude))
    .join(` ${filter.operation} `)})`;
}

export default ProductsCarousel;
